// Centralized utility to register UI contexts (modals, menus, panels, etc.)
// with the browser history stack so that pressing the OS/back button closes
// the active context instead of navigating away from the PWA.
//
// Usage:
//   import backNav from '@/utils/backNavigationManager.js'
//   // When opening an overlay/panel
//   const id = backNav.push(() => closeOverlay())
//   // When closing programmatically (e.g., via UI button)
//   backNav.remove(id, { goBack: true })
//
// Internally we keep a stack of contexts. On a popstate event we pop the
// latest context and invoke its onBack callback.

const BACK_KEY = '__app_back_ctx__'

const contexts = [] // Stack of { id, onBack }
// Use a counter instead of boolean to handle multiple rapid goBack calls
// This prevents race conditions when multiple UI elements close in quick succession
let skipPopCount = 0

function push(onBack) {
    const id = Date.now() + Math.random()
    const newState = { ...window.history.state, [BACK_KEY]: id }
    // Push a history entry that preserves the current router state
    window.history.pushState(newState, '')
    contexts.push({ id, onBack })
    return id
}

function remove(id, { goBack = false } = {}) {
    const idx = contexts.findIndex((c) => c.id === id)
    if (idx !== -1) {
        contexts.splice(idx, 1)
        if (goBack) {
            // We already handled UI closing; avoid calling onBack again in popstate
            // Increment counter instead of boolean to handle multiple rapid calls
            skipPopCount++
            window.history.back()
        }
    }
}

function handlePopState(e) {
    if (skipPopCount > 0) {
        // Skip this pop event that we triggered via remove()
        skipPopCount--
        return
    }
    // Always close the latest context first (LIFO), regardless of state id
    if (contexts.length > 0) {
        const ctx = contexts.pop()
        if (ctx?.onBack && typeof ctx.onBack === 'function') {
            ctx.onBack()
        }
    }
}

function clear() {
    contexts.length = 0
    console.log('Back navigation contexts cleared.')
}

if (typeof window !== 'undefined') {
    window.addEventListener('popstate', handlePopState)
}

export default {
    push,
    remove,
    clear,
}
